import React, { useContext, useState, useEffect } from 'react';
import { CampaignProps } from './types/propTypes';
import { useHttp } from 'src/hooks';
import { AuthContext } from 'src/contexts';

interface FileItem {
  id: number;
  file: File;
  previewUrl: string;
  isUploading?: boolean;
  isVideo?: boolean;
  thumbnailImageId?: number | null; // Track which image is the thumbnail for this video
}

const UploadCreativeForm: React.FC<CampaignProps> = ({
  setCampaignData,
  campaignData,
  setIsStepValid,
}) => {
  const { sendRequest } = useHttp();
  const { accessToken } = useContext(AuthContext);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    // Check if there are any videos without thumbnails
    const hasVideosWithoutThumbnails = files.some(
      file => file.isVideo && file.thumbnailImageId === null,
    );

    // Check if there are any files at all
    const hasFiles = files.length > 0;

    // Step is valid only if there are files and no videos without thumbnails
    const isValid = hasFiles && !hasVideosWithoutThumbnails;

    setIsStepValid?.(isValid);

    // Set validation error message if needed
    if (hasVideosWithoutThumbnails) {
      setValidationError('You need to add thumbnail images for uploaded videos');
    } else {
      setValidationError(null);
    }
  }, [files, setIsStepValid]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0 && campaignData.adAccountId) {
      const uploadedFile = e.target.files[0];
      const fileExtension = uploadedFile.name.split('.').pop()?.toLowerCase();

      const isImage = ['jpeg', 'jpg', 'png'].includes(fileExtension || '');
      const isVideo = ['mp4', '3gp', 'avi'].includes(fileExtension || '');

      if (!isImage && !isVideo) {
        console.error('Invalid file type selected');
        return;
      }

      const apiUrl = isImage
        ? `${process.env.REACT_APP_PLATFORM_API_URL}/ad-creative/upload-image`
        : `${process.env.REACT_APP_PLATFORM_API_URL}/ad-creative/upload-video`;

      const newFile: FileItem = {
        id: Date.now(),
        file: uploadedFile,
        previewUrl: URL.createObjectURL(uploadedFile),
        isUploading: true,
        isVideo: isVideo,
        thumbnailImageId: isVideo ? null : undefined, // Videos start with no thumbnail
      };

      setFiles(prevFiles => [...prevFiles, newFile]);

      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('adAccountId', campaignData.adAccountId);

      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'X-Api-Key': process.env.REACT_APP_API_KEY as string,
          },
        });

        if (!response.ok) {
          throw new Error('File upload failed');
        }

        const data = await response.json();

        if (isImage) {
          const imageData = Object.values(data?.imageHash.images)[0] as any;
          const uploadedHash = imageData.hash;
          const uploadedUrl = imageData.url;

          setCampaignData(prevData => ({
            ...prevData,
            creativeImageHashes: [...prevData.creativeImageHashes, uploadedHash],
            creativeImageUrls: [...(prevData.creativeImageUrls || []), uploadedUrl],
          }));

          // If this image might be a thumbnail for a video, check and update
          setFiles(prevFiles => {
            const videosWithoutThumbnails = prevFiles.filter(
              f => f.isVideo && f.thumbnailImageId === null,
            );

            if (videosWithoutThumbnails.length > 0) {
              // Associate this image as thumbnail for the first video without one
              return prevFiles.map(file =>
                file.id === videosWithoutThumbnails[0].id
                  ? { ...file, thumbnailImageId: newFile.id }
                  : file.id === newFile.id
                  ? { ...file, isUploading: false }
                  : file,
              );
            }

            return prevFiles.map(file =>
              file.id === newFile.id ? { ...file, isUploading: false } : file,
            );
          });
        } else {
          const videoId = data.videoId;
          const videoThumbnail = data.details?.picture;

          setCampaignData(prevData => ({
            ...prevData,
            creativeVideoIds: [...(prevData.creativeVideoIds || []), videoId],
            creativeVideoThumbnails: [...(prevData.creativeVideoThumbnails || []), videoThumbnail],
          }));

          setFiles(prevFiles =>
            prevFiles.map(file =>
              file.id === newFile.id
                ? {
                    ...file,
                    isUploading: false,
                    thumbnailImageId: videoThumbnail ? newFile.id : null,
                  }
                : file,
            ),
          );
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setFiles(prevFiles => prevFiles.filter(file => file.id !== newFile.id));
      }
    }
  };

  const handleRemoveFile = (id: number) => {
    setFiles(prevFiles => {
      const fileToRemove = prevFiles.find(file => file.id === id);
      if (!fileToRemove) return prevFiles;

      const fileExtension = fileToRemove.file.name.split('.').pop()?.toLowerCase();
      const isImage = ['jpeg', 'jpg', 'png'].includes(fileExtension || '');
      const isVideo = ['mp4', '3gp', 'avi'].includes(fileExtension || '');

      setCampaignData((prevData: any) => {
        if (isImage) {
          return {
            ...prevData,
            creativeImageHashes: prevData.creativeImageHashes.filter(
              (_img: any, index: number) => index !== prevFiles.indexOf(fileToRemove),
            ),
            creativeImageUrls: prevData.creativeImageUrls.filter(
              (_url: any, index: number) => index !== prevFiles.indexOf(fileToRemove),
            ),
          };
        } else if (isVideo) {
          return {
            ...prevData,
            creativeVideoIds: prevData.creativeVideoIds.filter(
              (_id: any, index: number) => index !== prevFiles.indexOf(fileToRemove),
            ),
            creativeVideoThumbnails: prevData.creativeVideoThumbnails.filter(
              (_thumb: any, index: number) => index !== prevFiles.indexOf(fileToRemove),
            ),
          };
        }
        return prevData;
      });

      // If removing an image that was a thumbnail for a video, reset the video's thumbnail
      if (isImage) {
        return prevFiles
          .map(file => (file.thumbnailImageId === id ? { ...file, thumbnailImageId: null } : file))
          .filter(file => file.id !== id);
      }

      return prevFiles.filter(file => file.id !== id);
    });
  };

  // Helper function to check if a video has a thumbnail
  const hasThumbnail = (file: FileItem) => {
    if (!file.isVideo) return true;
    if (file.thumbnailImageId === null) return false;
    return files.some(f => f.id === file.thumbnailImageId);
  };

  return (
    <div className="my-8 p-6 border border-[#E5E7E7] rounded-2xl">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-[28px] text-center font-semibold font-inter text-black mb-10">
          Upload your creative
        </h2>

        {/* Validation Error Message */}
        {validationError && (
          <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
            <p>{validationError}</p>
          </div>
        )}

        <div
          className={`${
            files.length === 0
              ? 'flex justify-center items-center h-64'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6'
          }`}
        >
          {/* Upload Box */}
          <div
            className={`relative border-2 border-dashed border-indigo-400 rounded-lg bg-indigo-50 p-8 text-center ${
              files.length === 0 ? 'w-96 h-64' : ''
            }`}
          >
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-10 h-10 text-indigo-600 mb-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 12 12 16.5m0 0L16.5 12m-4.5 4.5V3"
                />
              </svg>
              <p className="text-base font-medium text-black">
                {files.length === 0 ? 'Upload creative' : 'Upload another creative'}
              </p>
              <p className="text-sm text-gray-500">File type: mp4, 3gp, jpeg, jpg, png</p>
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".mp4, .3gp, .jpeg, .jpg, .png, .avi"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={handleFileUpload}
            />
          </div>

          {/* Uploaded Files */}
          {files.map((file, index) => {
            const fileExtension = file.file.name.split('.').pop()?.toLowerCase();
            const isImage = ['jpeg', 'jpg', 'png'].includes(fileExtension || '');
            const isVideo = ['mp4', '3gp', 'avi'].includes(fileExtension || '');

            return (
              <div
                key={file.id}
                className={`relative rounded-lg overflow-hidden border ${
                  file.isVideo && !hasThumbnail(file) ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {file.isUploading ? (
                  // Single loader block — FIXED
                  <div className="w-full h-40 flex items-center justify-center bg-gray-100">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                  </div>
                ) : (
                  <>
                    {isImage ? (
                      <img
                        src={file.previewUrl}
                        alt="Uploaded creative"
                        className="w-full h-40 object-cover"
                      />
                    ) : isVideo ? (
                      <div className="relative">
                        {hasThumbnail(file) ? (
                          <>
                            <img
                              src={campaignData.creativeVideoThumbnails?.[index]}
                              alt="Video Thumbnail"
                              className="w-full h-40 object-cover"
                            />
                            <button className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="white"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="white"
                                className="w-10 h-10"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 3l14 9-14 9V3z"
                                />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <div className="w-full h-40 bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-500">No thumbnail</span>
                          </div>
                        )}
                      </div>
                    ) : null}

                    {/* Remove & Thumbnail Warning */}
                    <button
                      onClick={() => handleRemoveFile(file.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow-md"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>

                    {file.isVideo && !hasThumbnail(file) && (
                      <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs p-1 text-center">
                        Needs thumbnail
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UploadCreativeForm;
